/**
 * JWT token management and auth API calls.
 *
 * Tokens are stored in localStorage. The access token is short-lived (15min)
 * and the refresh token is long-lived (7 days). On 401, apiFetch() attempts
 * one silent refresh before redirecting to /login.
 */

const ACCESS_TOKEN_KEY = "im_access_token"
const REFRESH_TOKEN_KEY = "im_refresh_token"

// ===== Token Storage =====

export function getAccessToken(): string | null {
  if (typeof window === "undefined") return null
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

export function getRefreshToken(): string | null {
  if (typeof window === "undefined") return null
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

export function setTokens(access: string, refresh: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, access)
  localStorage.setItem(REFRESH_TOKEN_KEY, refresh)
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

// ===== Auth API =====

export interface AuthUser {
  id: string
  email: string
  username: string
  role: string
  is_active: boolean
  tenant: { id: string; name: string; slug: string } | null
}

export interface LoginResponse {
  access: string
  refresh: string
  user: AuthUser
}

function getApiBaseUrl(): string {
  return process.env.NEXT_PUBLIC_API_URL || ""
}

export async function requestOtpApi(email: string): Promise<{ detail: string }> {
  const res = await fetch(`${getApiBaseUrl()}/api/auth/otp/request/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: "Request failed" }))
    throw new Error(body.detail || `HTTP ${res.status}`)
  }
  return res.json()
}

export async function verifyOtpApi(email: string, code: string): Promise<LoginResponse> {
  const res = await fetch(`${getApiBaseUrl()}/api/auth/otp/verify/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, code }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: "Invalid or expired code." }))
    throw new Error(body.detail || `HTTP ${res.status}`)
  }
  return res.json()
}

export async function refreshTokenApi(
  refreshToken: string,
): Promise<{ access: string; refresh: string }> {
  const res = await fetch(`${getApiBaseUrl()}/api/auth/refresh/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh: refreshToken }),
  })

  if (!res.ok) {
    throw new Error("Token refresh failed")
  }

  return res.json()
}

export async function verifyTokenApi(token: string): Promise<AuthUser> {
  const res = await fetch(`${getApiBaseUrl()}/api/auth/verify/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  })

  if (!res.ok) {
    throw new Error("Token verification failed")
  }

  return res.json()
}

export async function logoutApi(
  refreshToken: string,
  accessToken?: string | null,
): Promise<void> {
  await fetch(`${getApiBaseUrl()}/api/auth/logout/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh: refreshToken, access: accessToken }),
  }).catch(() => {
    // Best-effort — don't fail logout if the API is unreachable
  })
}

// ===== Auth-aware Fetch =====

let _refreshPromise: Promise<boolean> | null = null

/**
 * Try to refresh the access token using the stored refresh token.
 * Deduplicates concurrent refresh attempts.
 */
async function tryRefresh(): Promise<boolean> {
  if (_refreshPromise) return _refreshPromise

  _refreshPromise = (async () => {
    const refresh = getRefreshToken()
    if (!refresh) return false

    try {
      const tokens = await refreshTokenApi(refresh)
      setTokens(tokens.access, tokens.refresh)
      return true
    } catch {
      clearTokens()
      return false
    } finally {
      _refreshPromise = null
    }
  })()

  return _refreshPromise
}

/**
 * Fetch wrapper that injects JWT auth headers and handles 401 with silent refresh.
 * Drop-in replacement for `fetch()` in API modules.
 */
export async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const token = getAccessToken()
  const headers = new Headers(init?.headers)
  if (token) {
    headers.set("Authorization", `Bearer ${token}`)
  }

  let response = await fetch(input, { ...init, headers })

  // On 401, attempt one silent refresh and retry
  if (response.status === 401 && token) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      const newToken = getAccessToken()
      if (newToken) {
        headers.set("Authorization", `Bearer ${newToken}`)
        response = await fetch(input, { ...init, headers })
      }
    }

    // Still 401 after refresh — redirect to login
    if (response.status === 401 && typeof window !== "undefined") {
      clearTokens()
      window.location.href = "/login"
    }
  }

  return response
}

/**
 * Returns auth headers for non-fetch usage (e.g., XMLHttpRequest).
 */
export function getAuthHeaders(): Record<string, string> {
  const token = getAccessToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}
